import { useState } from 'react'
import { Platform, ScrollView, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Modal from '@ui/components/Modal'
import AnimatedModalPanel from '@ui/animated/AnimatedModalPanel'
import Glass from '@ui/elements/Glass'
import Button from '@ui/buttons/Button'
import Typography from '@ui/typography'
import CodeInput from '@ui/forms/CodeInput/CodeInput'
import { CODE_LENGTH } from '@user/modals/CustomerLogin/Strategies/types'
import { trpc, RouterOutputs } from 'api'
import type { Customer, VerifyRequestResponse } from '@ca/common/types'
import { colors } from '@ui/colors'

type FetchResult = RouterOutputs['customer']['fetchWithCode']

interface Props {
  customer: Customer | null
  verifyRes: VerifyRequestResponse | null
  onClose: () => void
  onSuccess: (data: FetchResult['data']) => void
}

export default function ParentAuthModal({
  customer,
  verifyRes,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation(['modals', 'auth', 'ui'])

  const [code, setCode] = useState('')
  const [invalid, setInvalid] = useState(false)

  const fetchWithCode = trpc.customer.fetchWithCode.useMutation()

  const isOpen = !!customer && !!verifyRes

  const displayLogin =
    verifyRes?.strategy === 'phone'
      ? customer?.mobile ?? verifyRes?.login ?? ''
      : verifyRes?.strategy === 'email'
        ? customer?.email ?? verifyRes?.login ?? ''
        : verifyRes?.login ?? ''

  const submit = (value: string) => {
    if (!verifyRes || !customer) return
    setInvalid(false)
    fetchWithCode.mutate(
      {
        login: verifyRes.login,
        strategy: verifyRes.strategy,
        code: value,
        id: customer.id,
      },
      {
        onSuccess: (result) => {
          onSuccess(result.data)
        },
        onError: () => {
          setInvalid(true)
        },
      }
    )
  }

  const onPress = () => {
    if (code.length !== CODE_LENGTH) return
    submit(code)
  }

  const setCodeValue = (value: string) => {
    setCode(value)
    setInvalid(false)
    if (value.length === CODE_LENGTH) {
      submit(value)
    }
  }

  return (
    <Modal enabled={isOpen} onClose={onClose} backdropClose>
      <AnimatedModalPanel>
        <Glass>
          <ScrollView
            overScrollMode="never"
            className="w-full"
            bounces={false}
            keyboardShouldPersistTaps="handled"
          >
            <View className="items-center gap-16 overflow-hidden p-16 pt-8">
              <View
                className={Platform.select({
                  default: 'items-center gap-4 pt-4',
                  web: 'items-center gap-6 pt-8',
                })}
              >
                <Typography variant="XLB" className="text-center">
                  {t('modals:parentAuth.title')}
                </Typography>
                <Typography
                  variant="MD"
                  color={colors.text50}
                  className="text-center"
                >
                  {t('modals:parentAuth.otpSentTo', {
                    login: displayLogin,
                  })}
                </Typography>
              </View>

              <View className="items-center gap-16">
                <CodeInput
                  invalid={invalid}
                  length={CODE_LENGTH}
                  value={code}
                  setValue={setCodeValue}
                />
                <Button
                  label={t('modals:parentAuth.signIn')}
                  iconEnd="arrow-narrow-right"
                  onPress={onPress}
                  loading={fetchWithCode.isPending}
                />
              </View>
            </View>
          </ScrollView>
        </Glass>
      </AnimatedModalPanel>
    </Modal>
  )
}
